import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CrwdBanner } from '@crewdle/ui-kit';

import { MemberBudgetService } from 'src/app/services/member-budget/member-budget.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { creditsToDollarLabel } from 'src/app/utils/credits';

/**
 * The member's monthly budget in the app shell's banner slot (admin console →
 * Budgets). Blocked (over the limit, hard stop): agents and chats are paused —
 * "Request more" asks the org admins, with an optional short message. Near the
 * limit (≥ the org's notify %): a dismissible warning, once per period.
 */
@Component({
  selector: 'app-member-budget-banner',
  standalone: true,
  imports: [FormsModule, TranslateModule, CrwdBanner],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (budget.blocked()) {
      <crwd-banner
        tone="attention"
        data-testid="budget-blocked"
        [actionLabel]="composing || budget.requested() ? '' : ('budget.request-more' | translate)"
        (action)="composing = true"
      >
        <span>{{ 'budget.blocked' | translate: limitParams }}</span>
        @if (budget.requested()) {
          <span class="budget-banner__sent" data-testid="budget-requested"> {{ 'budget.requested' | translate }}</span>
        } @else if (composing) {
          <span class="budget-banner__compose">
            <input
              class="budget-banner__input"
              type="text"
              maxlength="500"
              data-testid="budget-request-message"
              [(ngModel)]="message"
              [placeholder]="'budget.request-placeholder' | translate"
              [disabled]="sending"
              (keydown.enter)="send()"
            />
            <button type="button" class="budget-banner__send" data-testid="budget-request-send" [disabled]="sending" (click)="send()">
              {{ 'budget.request-send' | translate }}
            </button>
            <button type="button" class="budget-banner__cancel" [disabled]="sending" (click)="composing = false">
              {{ 'common.buttons.cancel' | translate }}
            </button>
          </span>
        }
      </crwd-banner>
    } @else if (budget.warning()) {
      <crwd-banner
        tone="attention"
        dismissible
        data-testid="budget-warning"
        [dismissAriaLabel]="'common.buttons.close' | translate"
        (dismissed)="budget.dismissWarning()"
      >
        {{ 'budget.warning' | translate: limitParams }}
      </crwd-banner>
    }
  `,
  styles: [`
    :host { display: block; }
    .budget-banner__compose { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; margin-left: 0.5rem; }
    .budget-banner__input { min-width: 14rem; padding: 0.25rem 0.5rem; border: 1px solid var(--border-color); border-radius: 0.375rem;
      background: var(--background-color, transparent); color: var(--text-color); font: inherit; font-size: 0.8125rem; }
    .budget-banner__send, .budget-banner__cancel { padding: 0.25rem 0.7rem; border-radius: 0.5rem; font: inherit; font-size: 0.75rem;
      font-weight: 500; cursor: pointer; }
    .budget-banner__send { border: none; background: var(--accent-color, #4c17ca); color: var(--accent-contrast, #fff); }
    .budget-banner__cancel { border: 1px solid var(--border-color); background: transparent; color: var(--text-secondary-color, #475569); }
    .budget-banner__send:disabled, .budget-banner__cancel:disabled { opacity: .5; cursor: default; }
    .budget-banner__sent { font-weight: 500; }
  `],
})
export class MemberBudgetBannerComponent {
  readonly budget = inject(MemberBudgetService);
  private readonly notification = inject(NotificationService);

  composing = false;
  sending = false;
  message = '';

  get limitParams(): Record<string, string> {
    const b = this.budget.budget();
    return {
      spent: creditsToDollarLabel(b?.spentCredits ?? 0),
      limit: creditsToDollarLabel(b?.limitCredits ?? 0),
    };
  }

  async send(): Promise<void> {
    if (this.sending) return;
    this.sending = true;
    try {
      await this.budget.requestIncrease(this.message);
      this.composing = false;
      this.message = '';
      void this.notification.display('budget.request-sent', 'success');
    } catch {
      void this.notification.display('budget.request-error', 'danger');
    } finally {
      this.sending = false;
    }
  }
}
